import * as Sentry from "@sentry/nextjs";
import { InngestMiddleware } from "inngest";
import { setDomainSentryTags } from "@/lib/sentry/domain-tags";

/**
 * Inngest middleware that extracts domain identifiers from event payloads
 * and sets them as Sentry tags for the duration of the function run.
 *
 * Also sets Inngest run/event tags so Sentry issues can be filtered by
 * attempt number (e.g. only errors on retry) and traced back to the
 * originating event id.
 *
 * Relies on the Sentry middleware running first to establish an isolation scope.
 * Tags are prefixed with `vibrant.` to avoid collisions with Inngest's own tags.
 *
 * Tag-setting is deferred to `transformInput` (not done directly in `onFunctionRun`)
 * because the Sentry middleware's `withIsolationScope` is only active inside the
 * function execution lifecycle — `onFunctionRun` itself runs outside that scope.
 * Setting tags in `transformInput` ensures they land on the per-function isolation
 * scope rather than the process-wide scope, preventing tag bleed between concurrent
 * Inngest functions.
 */
export function domainContextMiddleware() {
  return new InngestMiddleware({
    name: "vibrant/domain-context",
    init() {
      return {
        onFunctionRun({ ctx }) {
          return {
            transformInput() {
              setDomainSentryTags(ctx.event?.data);
              setInngestRunTags(ctx);
              return {};
            },
          };
        },
      };
    },
  });
}

/**
 * Sets Inngest run/event identifiers as Sentry tags on the current scope.
 * Lets us filter Sentry issues by retry attempt and pivot from an issue
 * back to the originating event in the Inngest dashboard.
 */
function setInngestRunTags(ctx: {
  runId?: string;
  attempt?: number;
  event?: { id?: string };
}): void {
  const tags: Record<string, string | number> = {};

  if (typeof ctx.runId === "string" && ctx.runId.length > 0) {
    tags["inngest.run_id"] = ctx.runId;
    const dashboardUrl = buildInngestDashboardUrl(ctx.runId);
    if (dashboardUrl) {
      tags["inngest.dashboard_url"] = dashboardUrl;
    }
  }
  if (typeof ctx.attempt === "number") {
    tags["inngest.attempt"] = ctx.attempt;
  }
  const eventId = ctx.event?.id;
  if (typeof eventId === "string" && eventId.length > 0) {
    tags["inngest.event_id"] = eventId;
  }

  if (Object.keys(tags).length > 0) {
    Sentry.getCurrentScope().setTags(tags);
  }
}

/**
 * Builds an Inngest cloud dashboard URL for a given run, so a Sentry issue
 * can pivot directly to the run timeline. Returns undefined when running
 * against the local Inngest dev server (no cloud dashboard exists for it).
 */
function buildInngestDashboardUrl(runId: string): string | undefined {
  if (process.env.INNGEST_DEV) {
    return undefined;
  }
  const env = process.env.INNGEST_DASHBOARD_ENV ?? "production";
  return `https://app.inngest.com/env/${env}/runs/${runId}`;
}
